import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Mail,
  Lock,
  Unlock,
  Save,
  Pencil,
  Trash2,
  Plus,
  Clock,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const LOCK_OPTIONS = [
  { value: 0, label: "No lock" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: "custom", label: "Custom…" },
];

function daysUntil(iso) {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function LettersPage() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  // Composer state
  const [editingId, setEditingId] = useState(null); // null = new, id = edit existing
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [lockDays, setLockDays] = useState(0);
  const [lockMode, setLockMode] = useState("preset"); // preset | custom
  const [customDays, setCustomDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/letters");
      setLetters(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setLockDays(0);
    setLockMode("preset");
    setCustomDays(7);
    setComposerOpen(true);
  };

  const startEdit = (letter) => {
    setEditingId(letter.id);
    setTitle(letter.title || "");
    setContent(letter.content || "");
    const days = letter.lock_days || 0;
    setLockDays(days);
    if (days === 0 || days === 30 || days === 60 || days === 90) {
      setLockMode("preset");
    } else {
      setLockMode("custom");
      setCustomDays(days);
    }
    setComposerOpen(true);
  };

  const save = async () => {
    if (!content.trim()) {
      toast.error("Write something first.");
      return;
    }
    const effectiveDays =
      lockMode === "custom" ? Math.max(0, parseInt(customDays || 0, 10)) : lockDays;
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/letters/${editingId}`, {
          title,
          content,
          lock_days: effectiveDays,
        });
        toast.success("Letter updated");
      } else {
        await api.post("/letters", { title, content, lock_days: effectiveDays });
        toast.success(
          effectiveDays > 0
            ? `Letter sealed for ${effectiveDays} day${effectiveDays === 1 ? "" : "s"}`
            : "Letter saved"
        );
      }
      setComposerOpen(false);
      load();
    } catch {
      toast.error("Could not save letter");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/letters/${id}`);
    toast.success("Letter deleted");
    load();
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="letters-back-button"
          >
            <Link to="/log">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to personal log
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <Mail strokeWidth={1.5} className="size-3.5" />
            Letters to future self
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Write a note for the version of you that's still ahead.
          </h1>
          <p className="text-base text-muted-foreground">
            Seal it for 30, 60, or 90 days. The content stays hidden until your lock date passes.
          </p>
        </div>

        {/* New letter button */}
        {!composerOpen && (
          <Button
            onClick={startNew}
            className="rounded-xl h-11"
            data-testid="letters-new-button"
          >
            <Plus strokeWidth={1.5} className="size-4 mr-1.5" />
            Write a new letter
          </Button>
        )}

        {/* Composer */}
        {composerOpen && (
          <Card className="rounded-2xl" data-testid="letter-composer">
            <CardHeader>
              <CardTitle className="text-lg font-display">
                {editingId ? "Edit letter" : "New letter"}
              </CardTitle>
              <CardDescription>
                Speak to the you of 30, 60, or 90 days from now. Be honest.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="letter-title">Title (optional)</Label>
                <Input
                  id="letter-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. About the move, A reminder for July…"
                  data-testid="letter-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="letter-content">Your letter</Label>
                <Textarea
                  id="letter-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={10}
                  placeholder="Dear future me…"
                  className="resize-y text-base leading-relaxed"
                  data-testid="letter-content-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Lock duration</Label>
                <Select
                  value={lockMode === "custom" ? "custom" : String(lockDays)}
                  onValueChange={(v) => {
                    if (v === "custom") {
                      setLockMode("custom");
                    } else {
                      setLockMode("preset");
                      setLockDays(parseInt(v, 10) || 0);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]" data-testid="letter-lock-select">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCK_OPTIONS.map((opt) => (
                      <SelectItem key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {lockMode === "custom" && (
                  <div className="flex items-center gap-2 pt-1">
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="e.g. 14"
                      className="w-[140px] h-11"
                      data-testid="letter-custom-days-input"
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {(() => {
                    const raw =
                      lockMode === "custom" ? parseInt(customDays, 10) : lockDays;
                    const d = Number.isFinite(raw) ? Math.max(0, raw) : 0;
                    if (d === 0)
                      return "No lock — you can read this letter immediately.";
                    return `Sealed for ${d} day${d === 1 ? "" : "s"}. You won't be able to read its content until then.`;
                  })()}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  onClick={save}
                  disabled={busy}
                  className="rounded-xl"
                  data-testid="letter-save-button"
                >
                  <Save strokeWidth={1.5} className="size-4 mr-1.5" />
                  {busy ? "Saving…" : editingId ? "Update letter" : "Save letter"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setComposerOpen(false)}
                  className="rounded-xl"
                  data-testid="letter-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Letters list */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-display">Your letters</CardTitle>
            <CardDescription>
              {loading
                ? "Loading…"
                : letters.length === 0
                ? "You haven't written any letters yet."
                : `${letters.length} letter${letters.length === 1 ? "" : "s"} saved.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {letters.map((letter) => (
              <LetterCard
                key={letter.id}
                letter={letter}
                onEdit={() => startEdit(letter)}
                onDelete={() => remove(letter.id)}
              />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function LetterCard({ letter, onEdit, onDelete }) {
  const locked = letter.is_locked;
  const remaining = locked ? daysUntil(letter.locked_until) : 0;
  return (
    <div
      data-testid={`letter-${letter.id}`}
      className={`rounded-2xl border-2 p-4 sm:p-5 transition-colors ${
        locked ? "border-primary/30 bg-primary/5" : "border-border bg-card"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {locked ? (
              <Lock strokeWidth={1.5} className="size-4 text-primary" />
            ) : (
              <Unlock strokeWidth={1.5} className="size-4 text-muted-foreground" />
            )}
            <h3 className="font-display text-lg font-bold truncate">
              {letter.title || "Untitled letter"}
            </h3>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock strokeWidth={1.5} className="size-3" />
              Written {fmtDate(letter.created_at)}
            </span>
            {locked && (
              <span className="inline-flex items-center gap-1 text-primary font-semibold">
                <Sparkles strokeWidth={1.5} className="size-3" />
                Opens in {remaining} {remaining === 1 ? "day" : "days"}
                {letter.locked_until && ` · ${fmtDate(letter.locked_until)}`}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            onClick={onEdit}
            className="rounded-xl size-9"
            data-testid={`letter-edit-${letter.id}`}
            aria-label="Edit letter"
          >
            <Pencil strokeWidth={1.5} className="size-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="rounded-xl size-9 text-muted-foreground hover:text-destructive"
                data-testid={`letter-delete-${letter.id}`}
                aria-label="Delete letter"
              >
                <Trash2 strokeWidth={1.5} className="size-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this letter?</AlertDialogTitle>
                <AlertDialogDescription>
                  This can't be undone. The letter will be gone forever.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid={`letter-confirm-delete-${letter.id}`}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {locked ? (
        <div
          className="rounded-xl border border-dashed border-primary/40 bg-background p-5 text-center"
          data-testid={`letter-locked-${letter.id}`}
        >
          <Lock strokeWidth={1.2} className="size-7 text-primary mx-auto mb-2" />
          <div className="font-semibold text-sm">
            Sealed until {fmtDate(letter.locked_until)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Come back in {remaining} {remaining === 1 ? "day" : "days"} to read it.
          </div>
        </div>
      ) : (
        <div className="text-sm sm:text-base whitespace-pre-wrap leading-relaxed">
          {letter.content}
        </div>
      )}
    </div>
  );
}
